import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type TrackPublication,
  type Participant,
  type LocalParticipant,
} from 'livekit-client';
import { useGameStore, useProximityState, useLocalBubbleType, useBubbleNames, EventBus, reactionUrl, findReaction } from '@campus/core';
import { liveKitService } from '../../bridge/GameBridge.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Button, Icon } from '../ds/index.js';
import { useHandsStore } from '../../stores/handsStore.js';
import { jwtRole } from '../../lib/jwtRole.js';
import { API_BASE } from '../../lib/apiBase.js';
import { apiHeaders } from '../people/membersStore.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface TileData {
  identity: string;       // userId ou "userId:screen" pour le partage d'écran
  displayName: string;
  isLocal: boolean;
  isSpeaking: boolean;
  videoTrack: RemoteTrack | null;
  /** Participant distant : micro et caméra publiés et actifs (boutons des admins). */
  micOn?: boolean | undefined;
  cameraOn?: boolean;
}

// ── Constantes visuelles ─────────────────────────────────────────────────────

const TILE_WIDTH     = 224;
const MIN_TILE_W     = 80;
/** Largeur minimale d'une vignette de la rangée du haut sur ordinateur (au-delà, la rangée défile). */
const MIN_STRIP_TILE_W = 128;
/** Après un défilement à la main, la rangée ne revient pas d'elle-même au début (prise de parole). */
const STRIP_USER_SCROLL_GRACE_MS = 8_000;
const SIDEBAR_W      = 80;
const SIDEBAR_H      = 60;

// ── Modération depuis une vignette (OWNER/ADMIN) ─────────────────────────────
// Comme dans Meet : coupe le micro ou la caméra de la personne, qui est
// prévenue et peut les rallumer. Le verrou 5 min reste dans la fiche Personnes.

function requestMediaOff(userId: string, displayName: string, kind: 'mic' | 'camera') {
  const slug = useGameStore.getState().currentZoneId?.split(':')[1] ?? '';
  fetch(`${API_BASE}/world/zones/${slug}/media-off`, {
    method: 'POST', headers: apiHeaders(true), body: JSON.stringify({ userId, kind }),
  }).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    EventBus.emit('ui:toast', { message: kind === 'mic' ? `Micro de ${displayName} coupé.` : `Caméra de ${displayName} coupée.`, tone: 'success' });
  }).catch((err) => {
    console.error('[VideoTiles] media-off error:', err);
    EventBus.emit('ui:toast', { message: 'Impossible de couper pour le moment, réessaie.', tone: 'danger' });
  });
}

/**
 * Bouton d'une vignette : montre l'état réel de l'appareil (micro ou caméra
 * allumé = icône normale, cliquer le coupe ; déjà coupé = icône barrée,
 * grisée). Avant, l'icône barrée était toujours affichée : on croyait tout
 * coupé.
 */
function ModerateButton({ icon, label, disabled, onClick, large }: { icon: 'mic' | 'mic-off' | 'video' | 'video-off'; label: string; disabled: boolean; onClick: () => void; large?: boolean }) {
  const box = large ? 36 : 28;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onDoubleClick={(e) => e.stopPropagation()}
      style={{
        width: box, height: box, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: 0, border: '1px solid var(--color-hud-border)', borderRadius: 'var(--radius-sm)',
        background: 'var(--color-hud)', color: 'var(--color-text-primary)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
      }}
    >
      <Icon name={icon} size={large ? 18 : 14} />
    </button>
  );
}

// ── Vignette individuelle ────────────────────────────────────────────────────
// RÈGLE : containerRef ne contient AUCUN enfant React.
// Le <video> LiveKit est injecté/retiré uniquement via useEffect.

// ── Réactions sur les vignettes (vue réunion, vignette en grand) ────────────
// Comme dans Meet : l'emoji envoyé par une personne monte depuis le bas de sa
// vignette. Sur le campus, il s'envole déjà au-dessus de l'avatar (Reactions.ts),
// mais la vue réunion le cache.

interface FloatingReaction { key: number; id: string; x: number }
let reactionSeq = 0;

function useTileReactions(userId: string, enabled: boolean): FloatingReaction[] {
  const [items, setItems] = useState<FloatingReaction[]>([]);
  useEffect(() => {
    if (!enabled || !userId) return;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const onReaction = ({ userId: from, emoji }: { userId: string; emoji: string }) => {
      if (from !== userId || !findReaction(emoji)) return;
      const item = { key: ++reactionSeq, id: emoji, x: Math.round(Math.random() * 28) };
      setItems((list) => [...list.slice(-11), item]);
      const t = setTimeout(() => { timers.delete(t); setItems((list) => list.filter((r) => r.key !== item.key)); }, 2_400);
      timers.add(t);
    };
    EventBus.on('reaction:received', onReaction);
    return () => { EventBus.off('reaction:received', onReaction); timers.forEach(clearTimeout); setItems([]); };
  }, [userId, enabled]);
  return items;
}

// ── Plein écran d'une vignette (partage d'écran ou caméra mise en avant) ────

type FullscreenEl = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FullscreenDoc = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => Promise<void> | void };

function useTileFullscreen(ref: React.RefObject<HTMLDivElement | null>): [boolean, () => void] {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const doc = document as FullscreenDoc;
    const sync = () => setIsFull(!!ref.current && (doc.fullscreenElement ?? doc.webkitFullscreenElement) === ref.current);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [ref]);
  const toggle = useCallback(() => {
    const doc = document as FullscreenDoc;
    const el = ref.current as FullscreenEl | null;
    if (!el) return;
    if ((doc.fullscreenElement ?? doc.webkitFullscreenElement) === el) {
      void (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      return;
    }
    try {
      const done = el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen?.();
      void Promise.resolve(done).catch(() => EventBus.emit('ui:toast', { message: 'Ton navigateur refuse le plein écran.', tone: 'warning' }));
    } catch {
      EventBus.emit('ui:toast', { message: 'Ton navigateur refuse le plein écran.', tone: 'warning' });
    }
  }, [ref]);
  return [isFull, toggle];
}

function Tile({ tile, width, height, onDoubleClick, onClick, labelSize, large, zoomable, reactions, fullscreenable }: {
  tile: TileData;
  /** Nombre (px) ou longueur CSS ; sans `height`, la vignette est en 16:9. */
  width: number | string;
  height?: number;
  onDoubleClick?: () => void;
  onClick?: () => void;
  labelSize?: number;
  /** Vue réunion : nom et boutons des admins plus grands. */
  large?: boolean;
  /** Partage d'écran en grand : zoom molette/pincement, glisser pour se déplacer. */
  zoomable?: boolean;
  /** Réactions de la personne affichées sur la vignette (vue réunion). */
  reactions?: boolean;
  /** Bouton « Plein écran » (vignette en grand). */
  fullscreenable?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const [isFull, toggleFull] = useTileFullscreen(tileRef);
  const { zoom, zoomBy, resetZoom, transform, panHandlers } = useTileZoom(containerRef, !!zoomable);
  const jwt = useGameStore((s) => s.jwt);
  const [hovered, setHovered] = useState(false);
  const userId = tile.identity.includes(':screen') ? '' : tile.identity;
  const handRaised = useHandsStore((s) => !!userId && s.raised.has(userId));
  const floating = useTileReactions(userId, !!reactions);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tile.videoTrack) return;

    const el = tile.videoTrack.attach();
    el.style.width = '100%';
    el.style.height = '100%';
    // Partage d'écran : en entier, jamais rogné (une fenêtre partagée n'est
    // presque jamais en 16:9 ; en « cover », ses bords étaient coupés).
    el.style.objectFit = tile.identity.includes(':screen') ? 'contain' : 'cover';
    if (tile.identity.includes(':screen')) el.style.background = '#000';
    container.appendChild(el);

    return () => {
      tile.videoTrack!.detach(el);
      if (el.parentNode === container) {
        container.removeChild(el);
      }
    };
  }, [tile.videoTrack]);

  // Qui parle : contour vert franc, doublé d'un halo (avant : accent pâle, peu visible).
  const borderColor = tile.isSpeaking ? 'var(--color-success)' : 'var(--color-border-default)';
  const initial = (tile.displayName?.[0] ?? '?').toUpperCase();
  const fontSize = labelSize ?? 12;
  const refHeight = height ?? 126;
  const circle = refHeight > 80 ? 52 : refHeight > 50 ? 36 : 24;
  const isScreen = tile.identity.includes(':screen');
  const label = tile.isLocal && !isScreen ? 'Toi' : tile.displayName;
  const role = jwtRole(jwt);
  const canModerate = !tile.isLocal && !isScreen && (role === 'OWNER' || role === 'ADMIN') && refHeight >= 50;

  return (
    <div
      ref={tileRef}
      className="pl-tile"
      data-tile-id={tile.identity}
      onDoubleClick={zoom > 1 ? resetZoom : isFull ? toggleFull : onDoubleClick}
      onClick={onClick}
      {...panHandlers}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHovered(false); }}
      title={zoomable ? (zoom > 1 ? 'Glisser pour te déplacer · double-clic pour revenir à 100 %' : 'Molette ou pincement pour zoomer') : onDoubleClick ? 'Double-clic pour agrandir' : undefined}
      style={{
        position: 'relative',
        width,
        ...(height !== undefined ? { height } : { aspectRatio: '16 / 9' }),
        boxSizing: 'border-box',
        borderRadius: 'var(--radius-lg)',
        border: `2px solid ${borderColor}`,
        overflow: 'hidden',
        background: 'var(--color-bg-surface)',
        boxShadow: tile.isSpeaking ? '0 0 0 2px var(--color-success), var(--shadow-md)' : 'var(--shadow-md)',
        flexShrink: 0,
        cursor: zoom > 1 ? 'grab' : (onDoubleClick || onClick) ? 'pointer' : 'default',
        transition: 'width 0.2s ease, height 0.2s ease, border-color 0.2s',
      }}
    >
      {/* Conteneur vidéo — géré impérativement, JAMAIS d'enfants React ici */}
      <div ref={containerRef} style={{ width: '100%', height: '100%', transformOrigin: '0 0', transform }} />

      {(zoomable || fullscreenable) && (
        <div className="pl-tile-zoom" onDoubleClick={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          {zoomable && (
            <>
              <button type="button" onClick={() => zoomBy(1 / 1.25)} disabled={zoom <= 1} aria-label="Dézoomer"><Icon name="minus" size={16} /></button>
              <button type="button" onClick={resetZoom} disabled={zoom <= 1} aria-label="Revenir à 100 %" className="pl-num">{Math.round(zoom * 100)} %</button>
              <button type="button" onClick={() => zoomBy(1.25)} disabled={zoom >= TILE_ZOOM_MAX} aria-label="Zoomer"><Icon name="plus" size={16} /></button>
            </>
          )}
          {fullscreenable && (
            <button type="button" onClick={toggleFull} aria-label={isFull ? 'Quitter le plein écran (Échap)' : 'Plein écran'} title={isFull ? 'Quitter le plein écran (Échap)' : 'Plein écran'}>
              <Icon name={isFull ? 'minimize' : 'maximize'} size={16} />
            </button>
          )}
        </div>
      )}

      {floating.length > 0 && (
        <div className="pl-tile-reactions" aria-hidden="true">
          {floating.map((r) => (
            <img key={r.key} src={reactionUrl(r.id)} alt="" draggable={false} style={{ left: `${r.x}%`, width: large ? 56 : 32, height: large ? 56 : 32 }} />
          ))}
        </div>
      )}

      {/* Initiale — visible seulement sans vidéo */}
      {!tile.videoTrack && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{
            width: circle, height: circle, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: tile.isLocal ? 'var(--color-primary)' : 'var(--color-bg-overlay)',
            color: tile.isLocal ? 'var(--color-on-primary)' : 'var(--color-text-primary)',
            font: `800 ${Math.round(circle * 0.42)}px/1 var(--font-display)`,
            userSelect: 'none',
          }}>
            {initial}
          </span>
        </div>
      )}

      {handRaised && (
        <div title="Main levée" aria-label="Main levée" style={{
          position: 'absolute', top: 6, left: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 6px', borderRadius: 'var(--radius-sm)',
          background: 'var(--color-hud)', color: 'var(--color-warning)',
          border: '1px solid var(--color-warning)',
          font: `700 ${fontSize}px/1.4 var(--font-body)`,
        }}>
          <Icon name="hand" size={fontSize + 2} />
          {refHeight > 60 && 'Main levée'}
        </div>
      )}

      {/* Nom — div frère, pas enfant du containerRef */}
      <div style={{
        position: 'absolute', left: large ? 10 : 6, bottom: large ? 10 : 6, maxWidth: `calc(100% - ${large ? 20 : 12}px)`,
        boxSizing: 'border-box', padding: large ? '4px 12px' : '2px 8px', borderRadius: 'var(--radius-sm)',
        background: 'var(--color-hud)', color: 'var(--color-text-primary)',
        font: `${large ? 600 : 500} ${fontSize}px/1.5 var(--font-body)`,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {/* Micro coupé (carte Trello 63) : se voit sans survoler la vignette. */}
        {tile.micOn === false && !isScreen && (
          <span role="img" aria-label="Micro coupé" title="Micro coupé" style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--color-danger)' }}>
            <Icon name="mic-off" size={fontSize + 1} />
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </div>

      {/* Boutons des admins, au survol (et au clavier) */}
      {canModerate && (
        <div style={{
          position: 'absolute', top: large ? 10 : 6, right: large ? 10 : 6, display: 'flex', gap: large ? 6 : 4,
          opacity: hovered ? 1 : 0, transition: 'opacity 0.15s', pointerEvents: hovered ? 'auto' : 'none',
        }}>
          <ModerateButton
            icon={tile.micOn ? 'mic' : 'mic-off'}
            label={tile.micOn ? `Micro allumé - couper celui de ${tile.displayName}` : 'Micro coupé'}
            disabled={!tile.micOn}
            large={!!large}
            onClick={() => requestMediaOff(tile.identity, tile.displayName, 'mic')}
          />
          <ModerateButton
            icon={tile.cameraOn ? 'video' : 'video-off'}
            label={tile.cameraOn ? `Caméra allumée - couper celle de ${tile.displayName}` : 'Caméra coupée'}
            disabled={!tile.cameraOn}
            large={!!large}
            onClick={() => requestMediaOff(tile.identity, tile.displayName, 'camera')}
          />
        </div>
      )}
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

export function VideoTiles() {
  const isMobile = useIsMobile();
  const tileWidthBase  = isMobile ? 100 : TILE_WIDTH;
  const sidebarW        = isMobile ? 56  : SIDEBAR_W;
  const sidebarH         = isMobile ? 42  : SIDEBAR_H;

  const { localBubbleId, playerBubbleIds, proximityCount, proximityUserIds } = useProximityState();
  const activeSpeakers   = useGameStore((s) => s.activeSpeakers);
  const liveKitConnected = useGameStore((s) => s.liveKitConnected);
  const zonePlayers      = useGameStore((s) => s.zonePlayers);
  const displayName      = useGameStore((s) => s.displayName);
  const podiumSpeakerId  = useGameStore((s) => s.podiumSpeakerId);

  const [revision, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((r) => r + 1), []);

  // Room qui porte l'AV du joueur local (zoneRoom normalement, meetingRoomRoom
  // pendant une réunion meeting-room) — getActiveAvRoom() n'est pas réactif
  // en lui-même (simple accesseur), donc ce compteur + l'écoute de
  // livekit:av-room-changed ci-dessous forcent un re-render (et un
  // ré-abonnement des RoomEvent) au moment précis où la room active bascule.
  // Sans ça, VideoTiles restait accroché à zoneRoom pendant toute une
  // réunion — plus aucune vidéo affichée pour personne (trouvé en review,
  // 2026-08-23).
  const [avRoomVersion, setAvRoomVersion] = useState(0);
  useEffect(() => {
    const onAvRoomChanged = () => setAvRoomVersion((v) => v + 1);
    EventBus.on('livekit:av-room-changed', onAvRoomChanged);
    return () => { EventBus.off('livekit:av-room-changed', onAvRoomChanged); };
  }, []);

  // Focus mode — purement local, pas dans gameStore
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // Partages d'écran déjà affichés en grand d'office (téléphone).
  const seenScreensRef = useRef(new Set<string>());
  const raisedHands = useHandsStore((s) => s.raised);

  const isScreenSharing  = useGameStore((s) => s.isScreenSharing);
  // Seul à un bureau personnel (bulle `desk:{id}`) : pas de vignette, elle
  // apparaît quand quelqu'un entre dans le bureau.
  const aloneAtDesk = !!localBubbleId?.startsWith('desk:') && proximityCount === 0 && !isScreenSharing;
  const visible = liveKitConnected && (!!podiumSpeakerId || (!aloneAtDesk && (!!localBubbleId || proximityCount > 0)));

  // Vue réunion (comme Meet), dans une salle de réunion sur ordinateur : on
  // reste sur le campus par défaut, le bouton « Vue réunion » l'ouvre pour
  // cette salle, « Voir le campus » ou Échap la referme. Sortir de la salle la ferme.
  const localBubbleType = useLocalBubbleType();
  const bubbleNames = useBubbleNames();
  const inMeetingRoom = !isMobile && localBubbleType === 'meeting-room' && !!localBubbleId;
  const [meetingFor, setMeetingFor] = useState<string | null>(null);
  useEffect(() => { if (meetingFor && meetingFor !== localBubbleId) setMeetingFor(null); }, [localBubbleId, meetingFor]);
  const meetingView = visible && inMeetingRoom && meetingFor === localBubbleId;
  // Place de la vignette principale en mode focus, mesurée
  const [focusAreaRef, focusArea] = useElementSize<HTMLDivElement>();

  // Bas de la rangée de vignettes : la caméra du jeu garde la carte visible en dessous.
  const [stripEl, setStripEl] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => { publishTilesBottom(stripEl); });
  useEffect(() => {
    if (!stripEl) return;
    const ro = new ResizeObserver(() => publishTilesBottom(stripEl));
    ro.observe(stripEl);
    return () => { ro.disconnect(); publishTilesBottom(null); };
  }, [stripEl]);

  // Échap → retour au mode grille, puis réduit la vue réunion
  useEffect(() => {
    if (!focusedId && !meetingView) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Échap ferme d'abord une fenêtre ouverte par-dessus.
      if (document.querySelector('[aria-modal="true"], [role="menu"]')) return;
      // Échap dans un panneau latéral (chat de réunion) ou en plein écran : pas pour la vue réunion.
      if ((e.target as HTMLElement | null)?.closest?.('aside') || document.fullscreenElement) return;
      if (focusedId) setFocusedId(null);
      else setMeetingFor(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusedId, meetingView, localBubbleId]);

  // Écouter les événements LiveKit pour mettre à jour les vignettes
  useEffect(() => {
    if (!visible) return;

    const room = liveKitService.getActiveAvRoom();
    if (!room) return;

    const onTrackSubscribed = (_track: RemoteTrack, pub: RemoteTrackPublication, _p: RemoteParticipant) => {
      if (pub.kind === Track.Kind.Video) bump();
    };
    const onTrackUnsubscribed = (_track: RemoteTrack, pub: RemoteTrackPublication, _p: RemoteParticipant) => {
      if (pub.kind === Track.Kind.Video) bump();
    };
    // Audio aussi : l'icône « micro coupé » à côté du nom suit le micro.
    const onTrackMuted = (_pub: TrackPublication, _p: Participant) => bump();
    const onTrackPublished = () => bump();
    const onLocalTrackPublished = () => bump();
    const onLocalTrackUnpublished = () => bump();
    const onParticipantConnected = () => bump();
    const onParticipantDisconnected = () => bump();

    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    room.on(RoomEvent.TrackMuted, onTrackMuted);
    room.on(RoomEvent.TrackUnmuted, onTrackMuted);
    room.on(RoomEvent.TrackPublished, onTrackPublished);
    room.on(RoomEvent.TrackUnpublished, onTrackPublished);
    room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    room.on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    // Filet de sécurité : les vignettes relisent l'état de la room régulièrement,
    // une caméra arrivée sans événement capté finit par s'afficher.
    const refresh = setInterval(bump, 3000);

    return () => {
      clearInterval(refresh);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
      room.off(RoomEvent.TrackMuted, onTrackMuted);
      room.off(RoomEvent.TrackUnmuted, onTrackMuted);
      room.off(RoomEvent.TrackPublished, onTrackPublished);
      room.off(RoomEvent.TrackUnpublished, onTrackPublished);
      room.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
      room.off(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    };
  }, [visible, bump, avRoomVersion]);

  // Ordre des vignettes distantes (userIds) : stable, sauf quand quelqu'un
  // qui prend la parole a sa vignette cachée par le défilement de la rangée :
  // il passe juste après « Toi », comme dans Meet. Jamais de réordre pour une
  // vignette déjà visible (sinon elles sauteraient à chaque prise de parole).
  const tileOrderRef = useRef<string[]>([]);
  useEffect(() => {
    if (activeSpeakers.length === 0) return;
    const strip = document.querySelector<HTMLElement>('.pl-tile-strip');
    if (!strip) return;
    const box = strip.getBoundingClientRect();
    const hidden = activeSpeakers.filter((id) => {
      const el = strip.querySelector(`[data-tile-id="${CSS.escape(id)}"]`);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.left < box.left - 1 || r.right > box.right + 1;
    });
    if (hidden.length === 0) return;
    tileOrderRef.current = [...hidden, ...tileOrderRef.current.filter((id) => !hidden.includes(id))];
    bump();
    // Revenir au début pour la montrer, sauf si l'on vient de faire défiler soi-même.
    const userScrollAt = Number(strip.dataset.userScrollAt ?? 0);
    if (strip.scrollLeft > 0 && Date.now() - userScrollAt > STRIP_USER_SCROLL_GRACE_MS) {
      strip.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [activeSpeakers, bump]);

  if (!visible) return null;

  const room = liveKitService.getActiveAvRoom();
  if (!room) return null;

  // ── Construire la liste des tiles ──────────────────────────

  const expectedRemoteIds = new Set<string>();
  if (localBubbleId) {
    for (const [userId, bid] of playerBubbleIds.entries()) {
      if (bid === localBubbleId) expectedRemoteIds.add(userId);
    }
  } else {
    for (const uid of proximityUserIds) {
      expectedRemoteIds.add(uid);
    }
  }
  // Podium : toujours inclure le speaker dans les tiles visibles
  if (podiumSpeakerId) {
    expectedRemoteIds.add(podiumSpeakerId);
  }

  const speakerSet = new Set(activeSpeakers);
  const tiles: TileData[] = [];

  const lp = room.localParticipant;
  const localVideoTrack = getVideoTrack(lp);
  const localDisplayName = displayName ?? lp.identity;
  expectedRemoteIds.delete(lp.identity);

  tiles.push({
    identity: lp.identity,
    displayName: localDisplayName,
    isLocal: true,
    isSpeaking: speakerSet.has(lp.identity),
    micOn: lp.isMicrophoneEnabled,
    videoTrack: localVideoTrack,
  });

  console.log('[VideoTiles] participants:',
    Array.from(room.remoteParticipants.values()).map(p => p.identity),
    'bubbleId:', localBubbleId,
    'proximity:', proximityCount,
    'expected:', [...expectedRemoteIds]);

  const ordered = tileOrderRef.current.filter((id) => expectedRemoteIds.has(id));
  for (const id of expectedRemoteIds) if (!ordered.includes(id)) ordered.push(id);
  tileOrderRef.current = ordered;

  for (const userId of ordered) {
    const rp = room.remoteParticipants.get(userId);
    const zonePlayer = zonePlayers.get(userId);
    const rpDisplayName = zonePlayer?.displayName ?? userId;

    tiles.push({
      identity: userId,
      displayName: rpDisplayName,
      isLocal: false,
      isSpeaking: speakerSet.has(userId),
      videoTrack: rp ? getRemoteVideoTrack(rp, Track.Source.Camera) : null,
      // Inconnu tant que la personne n'est pas dans la room LiveKit : pas d'icône.
      micOn: rp ? rp.isMicrophoneEnabled : undefined,
      cameraOn: rp?.isCameraEnabled ?? false,
    });

    if (rp) {
      const screenTrack = getRemoteVideoTrack(rp, Track.Source.ScreenShare);
      if (screenTrack) {
        console.log('[VideoTiles:screen] screen track détectée', userId, screenTrack.sid);
        tiles.push({
          identity: `${userId}:screen`,
          displayName: `${rpDisplayName} (écran)`,
          isLocal: false,
          isSpeaking: false,
          videoTrack: screenTrack,
        });
      }
    }
  }

  const localScreenTrack = getLocalTrack(lp, Track.Source.ScreenShare);
  if (localScreenTrack) {
    console.log('[VideoTiles:screen] screen track locale détectée', localScreenTrack.sid);
    tiles.push({
      identity: `${lp.identity}:screen`,
      displayName: `${localDisplayName} (écran)`,
      isLocal: true,
      isSpeaking: false,
      videoTrack: localScreenTrack,
    });
  }

  void revision;

  // Partages d'écran en tête : avec beaucoup de caméras, ils restaient au bout
  // de la bande, hors de vue (surtout sur téléphone).
  // Puis les mains levées, pour que la personne qui anime les voie sans chercher.
  const rank = (t: TileData) => (t.identity.includes(':screen') ? 2 : raisedHands.has(t.identity) ? 1 : 0);
  tiles.sort((a, b) => rank(b) - rank(a));

  // Téléphone : un partage d'écran qui commence s'affiche en grand d'office (une fois).
  const remoteScreenId = tiles.find((t) => !t.isLocal && t.identity.includes(':screen'))?.identity ?? null;
  if (isMobile && remoteScreenId && !seenScreensRef.current.has(remoteScreenId)) {
    seenScreensRef.current.add(remoteScreenId);
    if (!focusedId) queueMicrotask(() => setFocusedId(remoteScreenId));
  }

  // Si la vignette focalisée n'existe plus, retour au mode grille
  const focusedTile = focusedId ? tiles.find((t) => t.identity === focusedId) : null;
  const isFocusMode = focusedTile != null;

  // ── Vue réunion (salle de réunion) ─────────────────────────
  if (meetingView) {
    // Partage d'écran : affiché en grand d'office, les participants à côté.
    const featured = focusedTile ?? tiles.find((t) => t.identity.includes(':screen')) ?? null;
    return (
      <MeetingView
        roomName={bubbleNames.get(localBubbleId!) ?? 'Salle de réunion'}
        tiles={tiles}
        featured={featured}
        onFeature={(id) => setFocusedId(id)}
        onReduce={() => { setFocusedId(null); setMeetingFor(null); }}
      />
    );
  }

  // ── Mode focus (Google Meet / Teams layout) ────────────────
  if (isFocusMode) {
    const sidebarTiles = tiles.filter((t) => t.identity !== focusedId);

    return (
      <div
        style={{
          position: 'absolute', top: 0, left: 0, right: 'var(--pl-side-panel, 0px)',
          bottom: 'calc(var(--pl-hud-bottom) + 16px)', // au-dessus de la barre de contrôle
          display: 'flex',
          gap: 8,
          padding: '12px',
          background: 'var(--color-backdrop)',
          backdropFilter: 'blur(16px)',
          zIndex: 'var(--z-panel)' as unknown as number,
          pointerEvents: 'auto',
        }}
      >
        {/* Vignette principale */}
        <div ref={focusAreaRef} style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0,
        }}>
          <Tile
            tile={focusedTile}
            width={bestTileWidth(1, focusArea.w, focusArea.h)}
            reactions
            fullscreenable={!isMobile}
            zoomable={!isMobile}
            {...(isMobile ? { onClick: () => setFocusedId(null) } : { onDoubleClick: () => setFocusedId(null) })}
          />
        </div>

        {/* Colonne latérale */}
        {sidebarTiles.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            overflowY: 'auto',
            touchAction: 'pan-y',
            width: sidebarW + 4,
            flexShrink: 0,
            paddingRight: 4,
          }}>
            {sidebarTiles.map((tile) => (
              <Tile
                key={tile.identity}
                tile={tile}
                width={sidebarW}
                height={sidebarH}
                onClick={() => setFocusedId(tile.identity)}
                labelSize={9}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Mode grille (normal) ───────────────────────────────────

  const count = tiles.length;
  // Toujours sous la rangée de boutons du haut : la première ligne est à eux.
  // Avant, les vignettes se casaient dans la place entre les deux groupes de
  // boutons, qui dépend du nom de la carte, de la bulle et de la police :
  // vignette coupée sous « Vue réunion » chez Charles (2026-10-30).
  const available = isMobile ? 'calc(100vw - 24px)' : 'calc(100vw - var(--pl-side-panel, 0px) - 32px)';
  // Au-delà de ce que la place permet, les vignettes gardent une taille
  // lisible et la rangée défile (TileStrip) — avant, elles rétrécissaient
  // jusqu'à 80 px et les dernières étaient cachées sans le moindre indice.
  const minTile = isMobile ? MIN_TILE_W : MIN_STRIP_TILE_W;
  const tileWidth = `max(${minTile}px, min(${tileWidthBase}px, calc((${available} - ${8 * (count - 1) + (inMeetingRoom ? 128 : 0)}px) / ${count})))`;

  return (
    <div
      ref={setStripEl}
      style={{
        position: 'absolute',
        top: 'calc(var(--pl-hud-top) + 16px)',
        left: 'calc((100% - var(--pl-side-panel, 0px)) / 2)',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 'var(--z-hud)' as unknown as number,
        pointerEvents: 'auto',
        maxWidth: available,
      }}
    >
      <TileStrip showArrows={!isMobile}>
        {tiles.map((tile) => (
          <Tile
            key={tile.identity}
            tile={tile}
            width={tileWidth}
            // Téléphone : un toucher suffit (le double-toucher n'arrive pas toujours en dblclick).
            {...(isMobile ? { onClick: () => setFocusedId(tile.identity) } : { onDoubleClick: () => setFocusedId(tile.identity) })}
          />
        ))}
      </TileStrip>
      {/* Hors de la zone qui défile : toujours visible. */}
      {inMeetingRoom && (
        <Button variant="secondary" size="sm" icon="grid" onClick={() => { setFocusedId(null); setMeetingFor(localBubbleId); }} style={{ flexShrink: 0 }}>
          Vue réunion
        </Button>
      )}
    </div>
  );
}

// ── Rangée qui défile ────────────────────────────────────────────────────────

/**
 * Vignettes en rangée horizontale qui défile quand elles ne tiennent pas :
 * flèches aux bords (seulement du côté où il reste des vignettes, ordinateur),
 * fondu sur ce bord, molette = défilement horizontal, glisser au doigt.
 */
function TileStrip({ children, showArrows }: { children: React.ReactNode; showArrows: boolean }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((cur) => (cur.start === start && cur.end === end ? cur : { start, end }));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateEdges();
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    for (const child of el.children) ro.observe(child);
    const mo = new MutationObserver(() => {
      for (const child of el.children) ro.observe(child);
      updateEdges();
    });
    mo.observe(el, { childList: true });
    // Molette verticale → défilement horizontal (écouteur non passif pour
    // empêcher la page de défiler à la place).
    // Défilement à la main (molette, doigt) : noté pour que la prise de
    // parole ne ramène pas la rangée au début juste après (VideoTiles).
    const markUserScroll = () => { el.dataset.userScrollAt = String(Date.now()); };
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;
      markUserScroll();
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchmove', markUserScroll, { passive: true });
    return () => {
      ro.disconnect(); mo.disconnect();
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchmove', markUserScroll);
    };
  }, [updateEdges]);

  const page = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.dataset.userScrollAt = String(Date.now());
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  const fade = `linear-gradient(to right, ${edges.start ? 'transparent 0, #000 40px' : '#000 0'}, ${edges.end ? '#000 calc(100% - 40px), transparent 100%' : '#000 100%'})`;

  return (
    <div style={{ position: 'relative', minWidth: 0, display: 'flex' }}>
      <div
        ref={scrollerRef}
        onScroll={updateEdges}
        className="pl-tile-strip"
        style={{
          display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
          overflowX: 'auto', touchAction: 'pan-x', scrollbarWidth: 'none',
          maskImage: fade, WebkitMaskImage: fade,
        }}
      >
        {children}
      </div>
      {showArrows && edges.start && <StripArrow side="start" onClick={() => page(-1)} />}
      {showArrows && edges.end && <StripArrow side="end" onClick={() => page(1)} />}
    </div>
  );
}

function StripArrow({ side, onClick }: { side: 'start' | 'end'; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={side === 'start' ? 'Vignettes précédentes' : 'Vignettes suivantes'}
      onClick={onClick}
      style={{
        position: 'absolute', top: '50%', [side === 'start' ? 'left' : 'right']: 2, transform: 'translateY(-50%)',
        width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        border: '1px solid var(--color-hud-border)', borderRadius: '50%',
        background: 'var(--color-hud)', color: 'var(--color-text-primary)', cursor: 'pointer',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <Icon name={side === 'start' ? 'chevron-left' : 'chevron-right'} size={16} />
    </button>
  );
}

// ── Place prise en haut par les vignettes ────────────────────────────────────

let lastTilesBottom = 0;

/**
 * --pl-tiles-bottom sur <html> : bas de la rangée de vignettes (px de la
 * fenêtre), 0 sans rangée. ZoneScene (hudInsets) s'en sert comme marge du
 * haut pour que la salle et les avatars ne passent pas dessous.
 */
function publishTilesBottom(el: HTMLElement | null): void {
  const bottom = el ? Math.round(el.getBoundingClientRect().bottom) : 0;
  if (bottom === lastTilesBottom) return;
  lastTilesBottom = bottom;
  const root = document.documentElement;
  if (bottom) root.style.setProperty('--pl-tiles-bottom', `${bottom}px`);
  else root.style.removeProperty('--pl-tiles-bottom');
  EventBus.emit('ui:hud-insets-changed', {});
}

// ── Vue réunion ──────────────────────────────────────────────────────────────

const MEETING_GAP = 12;

/** Grille qui remplit au mieux w × h avec n vignettes 16:9 : largeur d'une vignette. */
function bestTileWidth(n: number, w: number, h: number): number {
  let best = 0;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const byW = (w - MEETING_GAP * (cols - 1)) / cols;
    const byH = ((h - MEETING_GAP * (rows - 1)) / rows) * 16 / 9;
    best = Math.max(best, Math.min(byW, byH));
  }
  return Math.max(MIN_TILE_W, Math.floor(best));
}

/** Taille mesurée d'un élément ; ref par callback, donc aussi pour un élément affiché plus tard. */
function useElementSize<T extends HTMLElement>(): [(el: T | null) => void, { w: number; h: number }] {
  const [el, setEl] = useState<T | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, size];
}

/**
 * Salle de réunion, façon Meet : participants en grandes vignettes qui
 * remplissent l'écran entre les boutons du haut et la barre du bas. Une
 * vignette mise en avant (partage d'écran, ou double-clic) prend la place
 * principale, les autres passent en colonne à droite.
 */
function MeetingView({ roomName, tiles, featured, onFeature, onReduce }: {
  roomName: string;
  tiles: TileData[];
  featured: TileData | null;
  onFeature: (id: string | null) => void;
  onReduce: () => void;
}) {
  const [areaRef, area] = useElementSize<HTMLDivElement>();
  const others = featured ? tiles.filter((t) => t.identity !== featured.identity) : tiles;
  const people = tiles.filter((t) => !t.identity.includes(':screen')).length;
  const sideW = featured && others.length > 0 ? Math.min(240, Math.max(160, Math.floor(area.w * 0.2))) : 0;
  const mainW = featured
    ? bestTileWidth(1, area.w - (sideW ? sideW + MEETING_GAP : 0), area.h)
    : bestTileWidth(tiles.length, area.w, area.h);

  return (
    <section
      aria-label={`Réunion : ${roomName}`}
      style={{
        position: 'absolute',
        top: 'calc(var(--pl-hud-top) + 16px)',
        left: 16, right: 'calc(16px + var(--pl-side-panel, 0px))',
        bottom: 'calc(var(--pl-hud-bottom) + 16px)',
        display: 'flex', flexDirection: 'column', gap: MEETING_GAP,
        padding: 16, boxSizing: 'border-box',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--color-backdrop)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        zIndex: 'var(--z-hud)' as unknown as number,
        pointerEvents: 'auto',
        color: 'var(--color-text-primary)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <h2 style={{ margin: 0, font: '700 16px/1.25 var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roomName}</h2>
          <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)' }}>
            {people} participant{people > 1 ? 's' : ''}{featured ? ' · double-clic pour revenir à la grille' : ' · double-clic sur une vignette pour l\u2019agrandir'}
          </span>
        </div>
        <Button variant="secondary" size="sm" icon="map" onClick={onReduce} title="Réduire la vue réunion (Échap)">
          Voir le campus
        </Button>
      </header>

      <div ref={areaRef} style={{ flex: 1, minHeight: 0, display: 'flex', gap: MEETING_GAP, alignItems: 'center', justifyContent: 'center' }}>
        {area.w > 0 && (featured ? (
          <>
            <div style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tile tile={featured} width={mainW} labelSize={17} large reactions fullscreenable zoomable onDoubleClick={() => onFeature(null)} />
            </div>
            {others.length > 0 && (
              <div style={{ width: sideW, height: '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', justifyContent: others.length * (sideW * 9 / 16 + 8) < area.h ? 'center' : 'flex-start' }}>
                {others.map((t) => (
                  <Tile key={t.identity} tile={t} width={sideW} labelSize={14} reactions onClick={() => onFeature(t.identity)} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: MEETING_GAP, justifyContent: 'center', alignContent: 'center', maxWidth: '100%', maxHeight: '100%' }}>
            {tiles.map((t) => (
              <Tile key={t.identity} tile={t} width={mainW} labelSize={17} large reactions onDoubleClick={() => onFeature(t.identity)} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Zoom d'un partage d'écran (carte Trello 69) ─────────────────────────────
// Molette ou pincement du pavé tactile (wheel + ctrlKey) autour du pointeur,
// glisser pour se déplacer, double-clic pour revenir à 100 %. Transformation
// CSS du conteneur vidéo seulement : la vidéo reçue ne change pas.

const TILE_ZOOM_MAX = 4;

function useTileZoom(ref: React.RefObject<HTMLDivElement | null>, enabled: boolean) {
  const [view, setView] = useState({ z: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

  // Garde le contenu dans la vignette (origine en haut à gauche).
  const clamp = (z: number, x: number, y: number) => {
    const el = ref.current;
    const w = el?.clientWidth ?? 0, h = el?.clientHeight ?? 0;
    return { z, x: Math.min(0, Math.max(w - w * z, x)), y: Math.min(0, Math.max(h - h * z, y)) };
  };
  // Zoom autour d'un point de la vignette (px, py relatifs à son coin).
  const zoomAt = (factor: number, px: number, py: number) => {
    const v = viewRef.current;
    const z = Math.min(TILE_ZOOM_MAX, Math.max(1, v.z * factor));
    const k = z / v.z;
    setView(clamp(z, px - (px - v.x) * k, py - (py - v.y) * k));
  };

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) { setView({ z: 1, x: 0, y: 0 }); return; }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault(); // sinon le navigateur zoome la page (pincement) ou fait défiler
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002)), e.clientX - r.left, e.clientY - r.top);
    };
    // Safari : le pincement du pavé tactile arrive en gesturestart/gesturechange
    // (`scale`, depuis le début du geste), pas en wheel.
    let lastScale = 1;
    const onGestureStart = (e: Event) => { e.preventDefault(); lastScale = 1; };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const { scale, clientX, clientY } = e as Event & { scale?: number; clientX?: number; clientY?: number };
      if (typeof scale !== 'number' || scale <= 0) return;
      const r = el.getBoundingClientRect();
      zoomAt(scale / lastScale, (clientX ?? r.left + r.width / 2) - r.left, (clientY ?? r.top + r.height / 2) - r.top);
      lastScale = scale;
    };
    const target = el.parentElement;
    target?.addEventListener('wheel', onWheel, { passive: false });
    target?.addEventListener('gesturestart', onGestureStart, { passive: false });
    target?.addEventListener('gesturechange', onGestureChange, { passive: false });
    return () => {
      target?.removeEventListener('wheel', onWheel);
      target?.removeEventListener('gesturestart', onGestureStart);
      target?.removeEventListener('gesturechange', onGestureChange);
    };
  }, [enabled]);

  const panHandlers = enabled ? {
    onPointerDown: (e: React.PointerEvent) => {
      if (viewRef.current.z <= 1 || (e.target as HTMLElement).closest('button')) return;
      drag.current = { px: e.clientX, py: e.clientY, x: viewRef.current.x, y: viewRef.current.y };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      setView(clamp(viewRef.current.z, d.x + e.clientX - d.px, d.y + e.clientY - d.py));
    },
    onPointerUp: () => { drag.current = null; },
    onPointerCancel: () => { drag.current = null; },
  } : {};

  const el = ref.current;
  return {
    zoom: view.z,
    zoomBy: (f: number) => zoomAt(f, (el?.clientWidth ?? 0) / 2, (el?.clientHeight ?? 0) / 2),
    resetZoom: () => setView({ z: 1, x: 0, y: 0 }),
    transform: view.z > 1 ? `translate(${view.x}px, ${view.y}px) scale(${view.z})` : undefined,
    panHandlers,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getVideoTrack(lp: LocalParticipant): RemoteTrack | null {
  return getLocalTrack(lp, Track.Source.Camera);
}

function getLocalTrack(lp: LocalParticipant, source: Track.Source): RemoteTrack | null {
  for (const pub of lp.trackPublications.values()) {
    if (
      pub.kind === Track.Kind.Video &&
      pub.source === source &&
      pub.track &&
      !pub.isMuted
    ) {
      return pub.track as unknown as RemoteTrack;
    }
  }
  return null;
}

function getRemoteVideoTrack(rp: RemoteParticipant, source: Track.Source): RemoteTrack | null {
  for (const pub of rp.trackPublications.values()) {
    if (
      pub.kind === Track.Kind.Video &&
      pub.source === source &&
      pub.isSubscribed &&
      pub.track &&
      !pub.isMuted
    ) {
      console.log(`[VideoTiles] getRemoteVideoTrack ${rp.identity} source=${source} sid=${pub.trackSid} subscribed=${pub.isSubscribed}`);
      return pub.track as RemoteTrack;
    }
  }
  return null;
}
